import React, { useState, useEffect } from 'react'
import { 
  SparklesIcon, 
  ReceiptPercentIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { memberLevelsApi } from '../../services/adminApi'

interface MemberLevel {
  id: number
  name: string
  name_en: string | null
  icon: string
  color: string
}

interface StudentMemberSettingsProps {
  studentId: number
  currentLevelId: number | null
  currentLevelName?: string | null
  currentLevelIcon?: string | null
  currentLevelColor?: string | null
  discountThreshold: number
  discountRate: number
  onUpdate?: () => void
}

export default function StudentMemberSettings({
  studentId,
  currentLevelId,
  currentLevelName,
  currentLevelIcon,
  currentLevelColor,
  discountThreshold,
  discountRate,
  onUpdate
}: StudentMemberSettingsProps) {
  const [levels, setLevels] = useState<MemberLevel[]>([])
  const [selectedLevelId, setSelectedLevelId] = useState<number | null>(currentLevelId)
  const [threshold, setThreshold] = useState(discountThreshold || 0)
  const [rate, setRate] = useState(discountRate || 1.0)
  const [saving, setSaving] = useState(false)
  const [showLevelSelect, setShowLevelSelect] = useState(false)

  // 加载会员等级列表
  useEffect(() => {
    const loadLevels = async () => {
      try {
        const response = await memberLevelsApi.getAll()
        if (response.success) {
          setLevels(response.data?.filter((l: any) => l.is_active) || [])
        }
      } catch (error) {
        console.error('加载会员等级失败:', error)
      }
    }
    loadLevels()
  }, [])

  // 保存会员等级
  const saveMemberLevel = async (levelId: number | null) => {
    setSaving(true)
    try {
      const response = await memberLevelsApi.assignToStudent({
        student_id: studentId,
        level_id: levelId
      })
      if (response.success) {
        setSelectedLevelId(levelId)
        setShowLevelSelect(false)
        toast.success('会员等级设置成功')
        onUpdate?.()
      } else {
        toast.error(response.error?.message || '设置失败')
      }
    } catch (error) {
      toast.error('设置失败，请重试')
    } finally {
      setSaving(false)
    }
  }

  // 保存折扣设置
  const saveDiscount = async () => {
    if (threshold > 0 && (rate <= 0 || rate > 1)) {
      toast.error('折扣率必须在 0.1-1.0 之间')
      return
    }

    setSaving(true)
    try {
      const response = await memberLevelsApi.setStudentDiscount({
        student_id: studentId,
        discount_threshold: threshold,
        discount_rate: rate
      })
      if (response.success) {
        toast.success('折扣设置成功')
        onUpdate?.()
      } else {
        toast.error(response.error?.message || '设置失败')
      }
    } catch (error) {
      toast.error('设置失败，请重试')
    } finally {
      setSaving(false)
    }
  }

  // 获取当前等级信息
  const currentLevel = selectedLevelId 
    ? levels.find(l => l.id === selectedLevelId)
    : null

  // 折扣显示文本
  const getDiscountText = () => {
    if (rate >= 1) return '无折扣'
    return `${(rate * 10).toFixed(1)} 折`
  }

  return (
    <div className="space-y-4">
      {/* 会员等级设置 */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-purple-600" />
            <h4 className="font-medium text-purple-900">会员等级</h4>
          </div>
        </div>
        
        {showLevelSelect ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              {/* 无等级选项 */}
              <button
                onClick={() => saveMemberLevel(null)}
                disabled={saving}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  selectedLevelId === null
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <span className="text-gray-400 text-lg">👤</span>
                <span className="ml-2 text-sm text-gray-600">无等级</span>
              </button>
              
              {levels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => saveMemberLevel(level.id)}
                  disabled={saving}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    selectedLevelId === level.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <span className="text-lg">{level.icon}</span>
                  <span className="ml-2 text-sm" style={{ color: level.color }}>
                    {level.name}
                  </span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowLevelSelect(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              取消
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {currentLevel || currentLevelId ? (
                <>
                  <span className="text-2xl">{currentLevel?.icon || currentLevelIcon || '👤'}</span>
                  <span 
                    className="font-medium"
                    style={{ color: currentLevel?.color || currentLevelColor || '#6B7280' }}
                  >
                    {currentLevel?.name || currentLevelName || '未知等级'}
                  </span>
                </>
              ) : (
                <span className="text-gray-400">暂未设置会员等级</span>
              )}
            </div>
            <button
              onClick={() => setShowLevelSelect(true)}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              修改
            </button>
          </div>
        )}
      </div>

      {/* 折扣设置 */}
      <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg p-4 border border-green-100">
        <div className="flex items-center gap-2 mb-3">
          <ReceiptPercentIcon className="w-5 h-5 text-green-600" />
          <h4 className="font-medium text-green-900">专属折扣</h4>
        </div>
        
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">满额门槛（元）</label>
              <input
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="0 表示不限"
                min="0"
                step="1"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">折扣率</label>
              <div className="relative">
                <input
                  type="number"
                  value={rate}
                  onChange={(e) => setRate(parseFloat(e.target.value) || 1)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="1.0"
                  min="0.1"
                  max="1.0"
                  step="0.05"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  {getDiscountText()}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-gray-500">
              {threshold > 0 
                ? `满 ¥${threshold} 享 ${getDiscountText()}`
                : '未设置折扣条件'}
            </p>
            <button
              onClick={saveDiscount}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <CheckIcon className="w-4 h-4" />
              保存
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

